import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './hook';

type VerifyStatus = 'pending' | 'verified' | 'rejected';

interface VerifiedRouteProps {
  /**
   * Confirms the session with the backend (e.g. `GET /me` through the
   * gateway). Resolve `true` if the caller may proceed, `false` otherwise.
   * Called once per mount/re-verify and whenever `isAuthenticated` becomes
   * true.
   */
  verify: () => Promise<boolean>;
  /** Rendered while `verify` is in flight. */
  loading: ReactNode;
  /**
   * Rendered once `verify` resolves `true`. Pair this with `React.lazy` in
   * the caller so the protected route's code is not downloaded by the
   * browser until the backend has actually confirmed the session — an
   * unauthenticated client should never receive more than the login page.
   */
  children: ReactNode;
  /** Called when `verify` resolves `false` or throws — typically `signOut`. */
  onRejected: () => void;
}

/**
 * Gates `children` behind a backend verification call, in addition to the
 * client-side `isAuthenticated` flag `useAuth` exposes. MSAL having a cached
 * account only proves the user signed in with Entra — it says nothing about
 * whether that identity resolves to a real, active user in this app, so
 * `children` is never rendered until `verify` actually confirms it.
 *
 * Status starts 'pending' and only ever advances on a `verify()` resolution
 * (never reset mid-mount), so a caller that flips `isAuthenticated` false→true
 * again without unmounting this component would briefly see a stale prior
 * result. Not a concern for a route-gated caller (e.g. this unmounts on
 * navigation to the login route), which is the only usage today.
 */
export function VerifiedRoute({ verify, loading, children, onRejected }: VerifiedRouteProps) {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>('pending');
  const onRejectedRef = useRef(onRejected);
  useLayoutEffect(() => {
    onRejectedRef.current = onRejected;
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    verify()
      .then((ok) => {
        if (!cancelled) setStatus(ok ? 'verified' : 'rejected');
      })
      .catch(() => {
        if (!cancelled) setStatus('rejected');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verify is expected to be stable per mount; re-running on every render identity change would re-hit the backend needlessly.
  }, [isAuthenticated]);

  useEffect(() => {
    if (status === 'rejected') onRejectedRef.current();
  }, [status]);

  if (!isAuthenticated || status !== 'verified') return <>{loading}</>;
  return <>{children}</>;
}
